import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Title } from '@siftscience/focus-components/text'
import { makeStyles } from '@material-ui/core/styles'
import { v4 as uuid } from 'uuid'

import { PRIMARY_TYPE_OPTIONS, PrimaryType, UIColumn } from './types'
import {
  getSchemaRequired,
  mapSchemaToUIColumns,
  toProvidedSchema
} from './helpers'
import { AddColumnInline } from './components/AddColumnInline'
import { ColumnSection } from './components/ColumnSection'
import { JSONSchema } from '../dto/template'
import { snake } from '../../../utils/snake'

interface EditableSchemaSectionsProps {
  schema: JSONSchema
  fieldName?: string
  onSchemaChange?: (schema: JSONSchema) => void
  onSave?: (payload: JSONSchema) => void
}

const useStyles = makeStyles(() => ({
  container: { width: '100%', padding: '24px 0' },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16
  },
  actions: { display: 'flex', alignItems: 'center', gap: 12 },
  list: { display: 'grid', gap: 12 }
}))

export const EditableSchemaTable = ({
  schema,
  fieldName,
  onSchemaChange
}: EditableSchemaSectionsProps) => {
  const classes = useStyles()

  const initialColumns = useMemo<UIColumn[]>(
    () => mapSchemaToUIColumns(schema, fieldName),
    [schema, fieldName]
  )
  const [columnsState, setColumnsState] = useState<UIColumn[]>(initialColumns)
  const [required, setRequired] = useState<string[]>(
    getSchemaRequired(schema, fieldName)
  )

  useEffect(() => {
    setColumnsState(mapSchemaToUIColumns(schema, fieldName))
    setRequired(getSchemaRequired(schema, fieldName))
  }, [schema, fieldName])

  const snapRef = useRef({ columnsState, required })
  snapRef.current = { columnsState, required }

  const emit = useCallback(() => {
    if (onSchemaChange) {
      onSchemaChange(
        toProvidedSchema(
          snapRef.current.columnsState,
          snapRef.current.required,
          fieldName
        )
      )
    }
  }, [onSchemaChange, fieldName])

  const addColumn = useCallback(
    (title: string, keyInput?: string) => {
      const key = snake(keyInput || title)
      if (!key) return
      if (columnsState.some(c => c.key === key)) return

      const newCol: UIColumn = {
        uiId: uuid(),
        key,
        type: ['string', 'null'],
        title: title || key
      }
      setColumnsState(prev => [...prev, newCol])
      setTimeout(emit, 0)
    },
    [columnsState, emit]
  )

  const renameColumn = useCallback(
    (oldKey: string, newKeyRaw: string, newTitle?: string) => {
      const newKey = snake(newKeyRaw)
      if (!newKey) return
      if (oldKey !== newKey && columnsState.some(c => c.key === newKey)) return

      setColumnsState(prev =>
        prev.map(col =>
          col.key === oldKey
            ? { ...col, key: newKey, title: newTitle?.trim() || col.title }
            : col
        )
      )
      setRequired(prev => prev.map(key => (key === oldKey ? newKey : key)))
      setTimeout(emit, 0)
    },
    [columnsState, emit]
  )

  const deleteColumn = useCallback(
    (key: string) => {
      if (required.includes(key)) return
      setColumnsState(prev => prev.filter(col => col.key !== key))
      setTimeout(emit, 0)
    },
    [required, emit]
  )

  const toggleRequired = useCallback(
    (key: string) => {
      setRequired(prev => {
        const next = prev.includes(key)
          ? prev.filter(k => k !== key)
          : [...prev, key]
        setTimeout(emit, 0)
        return next
      })
    },
    [emit]
  )

  const changePrimaryType = useCallback(
    (colKey: string, nextPrimary: PrimaryType) => {
      setColumnsState(prev =>
        prev.map(col =>
          col.key === colKey
            ? { ...col, type: [nextPrimary, 'null'] as unknown as string[] }
            : col
        )
      )
      setTimeout(emit, 0)
    },
    [emit]
  )

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Title size="small">Schema fields</Title>
        <div className={classes.actions}>
          <AddColumnInline
            onAdd={addColumn}
            existingKeys={columnsState.map(c => c.key)}
          />
        </div>
      </div>

      <div className={classes.list}>
        {columnsState.map(col => {
          const primary = (
            Array.isArray(col.type)
              ? col.type.find(t =>
                  (PRIMARY_TYPE_OPTIONS as readonly string[]).includes(t)
                )
              : undefined
          ) as PrimaryType | undefined
          const selectedPrimary: PrimaryType = primary || 'string'

          return (
            <ColumnSection
              key={col.uiId}
              col={col}
              isRequired={required.includes(col.key)}
              onRename={renameColumn}
              onToggleRequired={toggleRequired}
              onDelete={deleteColumn}
              selectedPrimaryType={selectedPrimary}
              onChangePrimaryType={next =>
                changePrimaryType(col.key, next as PrimaryType)
              }
            />
          )
        })}
      </div>
    </div>
  )
}
